/**
 * suppressBrowserWarnings.js - Suppress browser warnings and console noise
 * 
 * This utility handles browser warnings that can't be controlled by the logger system,
 * such as manifest.json warnings, PWA warnings, and other browser-generated messages.
 */

// Store original console methods
const originalConsole = {
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error,
  debug: console.debug
};

// Configuration for what to suppress
let suppressConfig = {
  enabled: false,
  suppressManifestWarnings: true,
  suppressPWAWarnings: true,
  suppressPerformanceWarnings: true,
  suppressResourceWarnings: true,
  suppressResizeObserverErrors: true
};

// Patterns to match browser warnings
const WARNING_PATTERNS = {
  manifest: [
    /manifest\.json.*enctype/i,
    /mobile-web-app-capable.*deprecated/i,
    /apple-mobile-web-app-capable.*deprecated/i
  ],
  pwa: [
    /service worker.*bypassing cache/i,
    /service worker.*activating/i,
    /service worker.*install/i
  ],
  performance: [
    /requestAnimationFrame.*took.*ms/i,
    /Forced reflow.*took.*ms/i,
    /setInterval.*took.*ms/i
  ],
  resource: [
    /resource.*preloaded.*not used/i,
    /link preload.*not used/i
  ],
  resizeObserver: [
    /ResizeObserver loop completed with undelivered notifications/i,
    /ResizeObserver loop limit exceeded/i
  ]
};

// Check if a message matches any suppression pattern
const shouldSuppress = (message, level) => {
  if (!suppressConfig.enabled) return false;
  
  const messageStr = typeof message === 'string' ? message : String(message);
  
  // Check manifest warnings
  if (suppressConfig.suppressManifestWarnings) {
    if (WARNING_PATTERNS.manifest.some(pattern => pattern.test(messageStr))) {
      return true;
    }
  }
  
  // Check PWA warnings
  if (suppressConfig.suppressPWAWarnings) {
    if (WARNING_PATTERNS.pwa.some(pattern => pattern.test(messageStr))) {
      return true;
    }
  }
  
  // Check performance warnings
  if (suppressConfig.suppressPerformanceWarnings) {
    if (WARNING_PATTERNS.performance.some(pattern => pattern.test(messageStr))) {
      return true;
    }
  }
  
  // Check resource warnings
  if (suppressConfig.suppressResourceWarnings) {
    if (WARNING_PATTERNS.resource.some(pattern => pattern.test(messageStr))) {
      return true;
    }
  }
  
  // Check ResizeObserver warnings
  if (suppressConfig.suppressResizeObserverErrors) {
    if (WARNING_PATTERNS.resizeObserver.some(pattern => pattern.test(messageStr))) {
      return true;
    }
  }
  
  return false;
};

// Create suppressed console methods
const createSuppressedConsole = (level) => {
  return (...args) => {
    // Check if we should suppress this message
    if (shouldSuppress(args[0], level)) {
      return; // Don't log
    }
    
    // Otherwise, use original console method
    originalConsole[level](...args);
  };
};

// Apply suppression
export const applySuppression = (config = {}) => {
  suppressConfig = { ...suppressConfig, ...config };
  
  // TEMPORARILY DISABLED: Let's see if this was causing the function(){} issue
  // If console works after this, we know suppressBrowserWarnings was the problem.
  // Flip this flag to false to re-enable the console-override logic below.
  const SUPPRESSION_DISABLED = true;
  if (SUPPRESSION_DISABLED) return;

  if (suppressConfig.enabled) {
    console.log = createSuppressedConsole('log');
    console.info = createSuppressedConsole('info');
    console.warn = createSuppressedConsole('warn');
    console.debug = createSuppressedConsole('debug');
    // Don't suppress errors
    // console.error = createSuppressedConsole('error');
  } else {
    // Restore original methods
    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.warn = originalConsole.warn;
    console.debug = originalConsole.debug;
  }
};

// TEMPORARILY DISABLED: Initialize with enabled state for ResizeObserver errors
// applySuppression({ 
//   enabled: true,
//   suppressResizeObserverErrors: true 
// });

// TEMPORARILY DISABLED: Additional global ResizeObserver error suppression
// if (typeof window !== 'undefined') {
//   const originalConsoleError = console.error;
//   
//   console.error = (...args) => {
//     const message = args.join(' ');
//     if (message.includes('ResizeObserver loop completed with undelivered notifications') ||
//         message.includes('ResizeObserver loop limit exceeded')) {
//       // Suppress ResizeObserver errors
//       return;
//     }
//     originalConsoleError.apply(console, args);
//   };
//
//   // Handle unhandled promise rejections
//   window.addEventListener('unhandledrejection', (event) => {
//     if (event.reason && event.reason.message && 
//         (event.reason.message.includes('ResizeObserver loop completed with undelivered notifications') ||
//          event.reason.message.includes('ResizeObserver loop limit exceeded'))) {
//       event.preventDefault();
//       return false;
//     }
//   });
//
//   // Handle regular errors - this catches the React error overlay errors
//   window.addEventListener('error', (event) => {
//     if (event.message && 
//         (event.message.includes('ResizeObserver loop completed with undelivered notifications') ||
//          event.message.includes('ResizeObserver loop limit exceeded'))) {
//       event.preventDefault();
//       event.stopPropagation();
//       event.stopImmediatePropagation();
//       return false;
//     }
//   }, true); // Use capture phase to catch early
//   
//   // Override the ResizeObserver constructor to wrap callbacks
//   const OriginalResizeObserver = window.ResizeObserver;
//   window.ResizeObserver = class extends OriginalResizeObserver {
//     constructor(callback) {
//       super((entries, observer) => {
//         window.requestAnimationFrame(() => {
//           callback(entries, observer);
//         });
//       });
//     }
//   };
// }